import React from 'react';
import { useTranslation } from 'react-i18next';
import type { Contract, Milestone } from '../types';
import { contractTitle } from '../utils/contractTitle';
import type { MilestoneActionMode } from './MilestoneTimelineActions';
import { milestoneAttestationActionLabel } from '../utils/milestoneAttestation';
import { Notice } from './Notice';
import {
  isObjectionStep,
  overviewStatusCounts,
  type OverviewContractRow,
  type OverviewNextStep,
  type OverviewSide,
  type OverviewTotals
} from '../utils/projectStatusOverview';

export interface ProjectStatusMenuItem {
  key: string;
  label: string;
  onClick: () => void;
}

interface ProjectStatusOverviewProps {
  rows: OverviewContractRow[];
  totals: OverviewTotals;
  /** Payments on their way (not yet received), in SEK. */
  pendingPayment?: number;
  /** A party, or 'viewer' for an invited lawyer or inspector: no actions or payments. */
  side: OverviewSide;
  /** The project's status badge (existing project status logic). */
  statusBadge: React.ReactNode;
  /** Exception, completion and inactivity notices, shown under the status sentence. */
  notices?: React.ReactNode;
  menuItems: ProjectStatusMenuItem[];
  formatCurrency: (amount: number) => string;
  onOpenMilestone: (milestone: Milestone, mode?: MilestoneActionMode) => void;
  /** Company side: resubmits a remedied stage, then opens its attestation. */
  onAttestRemedied?: (milestone: Milestone) => void;
  preparingMilestoneId?: string | null;
  onSign: (contract: Contract) => void;
  signingDisabled?: boolean;
  onPay: (contract: Contract) => void;
  /** Scrolls to the contract's frame further down the page. */
  onShowContract: (root: Contract) => void;
  /** Whose turn it is in a stage's objection dialogue (who wrote last). */
  objectionTurn?: (milestone: Milestone) => 'person' | 'company';
  /**
   * More things the viewer has to do in a contract (e.g. a silence case),
   * shown under its "Nästa steg"; null when there are none.
   */
  renderContractActions?: (contractIds: string[]) => React.ReactNode;
}

const STATUS_TONE: Record<OverviewContractRow['status'], string> = {
  inProgress: 'is-primary',
  objection: 'is-warning',
  conflict: 'is-danger',
  awaitingPayment: 'is-warning',
  paymentPending: 'is-warning',
  signing: 'is-secondary',
  closed: 'is-success'
};

/**
 * "Projektstatus" on the project page: the whole project at a glance — its
 * status, what the viewer does next in each contract, and the money across
 * all contracts. The timeline itself stays in the contract frames below.
 */
export function ProjectStatusOverview({
  rows, totals, pendingPayment = 0, side, statusBadge, notices, menuItems, formatCurrency,
  onOpenMilestone, onAttestRemedied, preparingMilestoneId, onSign, signingDisabled, onPay, onShowContract, objectionTurn, renderContractActions
}: ProjectStatusOverviewProps) {
  const { t } = useTranslation();
  const isPerson = side === 'person';
  const isViewer = side === 'viewer';
  const [menuOpen, setMenuOpen] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!menuOpen) return;
    const close = (event: MouseEvent | KeyboardEvent) => {
      if (event instanceof KeyboardEvent ? event.key === 'Escape' : !menuRef.current?.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', close);
    };
  }, [menuOpen]);

  const contractName = (row: OverviewContractRow) => contractTitle(row.root)
    ? t('projectView.overview.contractTitle', { number: row.root.contractNumber, title: contractTitle(row.root) })
    : t('projectView.overview.contractNumber', { number: row.root.contractNumber });
  const amendments = (row: OverviewContractRow) => row.amendmentNumbers.length > 0
    ? t('combinedTimeline.includesAmendments', { numbers: row.amendmentNumbers.map(number => `#${number}`).join(', ') })
    : null;
  const statusLabel = (row: OverviewContractRow) => t(`projectView.overview.status.${row.status}`);

  const counts = overviewStatusCounts(rows);
  const sentence = rows.length === 0
    ? t('projectView.overview.sentenceEmpty')
    : t('projectView.overview.sentence', {
      count: rows.length,
      parts: counts.map(({ status, count }) => t(
        status === 'awaitingPayment' && !isPerson
          ? 'projectView.overview.part.awaitingCustomerPayment'
          : `projectView.overview.part.${status}`,
        { count }
      )).join(', ')
    });

  // A stage often carries its number as its name ("Etappmål #2"); say it once.
  const stageLabel = (milestone: Milestone) => {
    const numberLabel = t('projectView.overview.next.stageNumber', { number: milestone.milestoneNumber });
    const description = milestone.description?.trim();
    return description && description.toLocaleLowerCase() !== numberLabel.toLocaleLowerCase()
      ? t('projectView.overview.next.stage', { number: milestone.milestoneNumber, description })
      : numberLabel;
  };
  const subject = (milestone: Milestone) => t('projectView.dispute.objectionNumber', { number: milestone.milestoneNumber });

  // An objection is a notice at its stage, per viewer (ClickUp 869fjrytd):
  // the party whose turn it is gets the action under "Nästa steg", the other
  // one sees that it waits next to the status.
  const objectionMyTurn = (milestone: Milestone) => (objectionTurn?.(milestone) ?? 'company') === side;
  const isWaitingObjection = (step: OverviewNextStep) => !isViewer && isObjectionStep(step) &&
    !objectionMyTurn(step.milestone);
  const objectionNotice = (milestone: Milestone, context?: string) => {
    const turn = objectionTurn?.(milestone) ?? 'company';
    const myTurn = turn === side;
    const stage = stageLabel(milestone);
    return (
      <Notice
        level={myTurn ? 'action' : 'warning'}
        icon={myTurn ? undefined : 'clock'}
        context={context}
        testId={`project-status-objection-${milestone.id}`}
        title={myTurn
          ? t('projectView.overview.notice.objectionTitle', { stage })
          : t(turn === 'company' ? 'projectView.overview.notice.waitingCompany' : 'projectView.overview.notice.waitingPerson', { stage })}
        action={{
          label: t(myTurn
            ? isPerson ? 'projectView.overview.notice.showReply' : 'projectView.overview.notice.showObjection'
            : 'projectView.overview.notice.openObjection'),
          onClick: () => onOpenMilestone(milestone),
          testId: `project-status-conflict-${milestone.id}`
        }}
      >
        {t(myTurn
          ? isPerson ? 'projectView.overview.notice.personTurn' : 'projectView.overview.notice.companyTurn'
          : isPerson ? 'projectView.overview.notice.personWaits' : 'projectView.overview.notice.companyWaits')}
      </Notice>
    );
  };

  const renderStep = (row: OverviewContractRow, step: OverviewNextStep, index: number) => {
    const key = `${row.contract.id}-${index}`;
    const action = (label: string, onClick: () => void, options: { testId: string; secondary?: boolean; busy?: boolean; disabled?: boolean }) => (
      <button type="button" className={`btn ${options.secondary ? 'btn-outline-primary' : 'btn-primary'} project-status-next-button`}
        data-testid={options.testId} disabled={options.busy || options.disabled} onClick={onClick}>
        {options.busy && <span className="spinner-border spinner-border-sm me-1" aria-hidden="true" />}
        {label}
      </button>
    );
    const line = (text: string, button?: React.ReactNode, quiet = false) => (
      <li key={key} className="project-status-next-row">
        <span className={quiet ? 'project-status-next-text is-quiet' : 'project-status-next-text'}>{text}</span>
        {button}
      </li>
    );
    switch (step.kind) {
      case 'attest':
        return line(stageLabel(step.milestone), action(
          milestoneAttestationActionLabel(t('personHome.projectTimeline.attest', 'Attestera etappmål'), step.milestone),
          () => onOpenMilestone(step.milestone),
          { testId: `project-status-attest-${step.milestone.id}` }
        ));
      case 'remedy':
        return line(t('projectView.overview.next.remedy', { number: step.milestone.milestoneNumber }), isPerson
          ? action(t('projectView.dispute.openConflictManagement'), () => onOpenMilestone(step.milestone, 'resolution'),
            { testId: `project-status-conflict-${step.milestone.id}`, secondary: true })
          : action(
            milestoneAttestationActionLabel(t('personHome.projectTimeline.attest', 'Attestera etappmål'), step.milestone),
            () => (onAttestRemedied ? onAttestRemedied(step.milestone) : onOpenMilestone(step.milestone)),
            { testId: `project-status-remedy-${step.milestone.id}`, busy: preparingMilestoneId === step.milestone.id }
          ));
      case 'stage':
        return line(t('projectView.overview.next.currentStage', {
          stage: stageLabel(step.milestone),
          // e.g. ready_for_attestation → projectFunding.deliveryReadyForAttestation
          status: t(`projectFunding.delivery${(step.milestone.deliveryStatus ?? 'not_started')
            .split('_').map(part => part.charAt(0).toUpperCase() + part.slice(1)).join('')}`)
        }));
      case 'conflict': {
        // Terminology ladder: an objection (invändning) comes before a conflict and a dispute (tvist).
        // Conflict mode once started stays conflict, also while the stage is remedied.
        const { deliveryStatus, conflictStartedAt } = step.milestone;
        const conflictKey = deliveryStatus === 'external_dispute' ? 'projectView.overview.next.externalDispute'
          : conflictStartedAt || deliveryStatus === 'conflict' ? 'projectView.overview.next.conflict'
            : deliveryStatus === 'under_resolution' ? 'projectView.overview.next.remedy'
              : 'projectView.overview.next.objection';
        const conflictLine = t(conflictKey, { number: step.milestone.milestoneNumber });
        if (isViewer) return line(conflictLine);
        // An objection is a notice at its stage, per viewer (ClickUp 869fjrytd):
        // the party whose turn it is gets the action, the other one waits.
        if (conflictKey === 'projectView.overview.next.objection') {
          return <li key={key} className="project-status-next-row is-notice">{objectionNotice(step.milestone)}</li>;
        }
        return line(conflictLine, action(
          step.milestone.deliveryStatus === 'external_dispute'
            ? t('projectView.dispute.showDisputeExport')
            : t(isPerson ? 'projectView.dispute.manageSubject' : 'projectView.dispute.respondSubject', { subject: subject(step.milestone) }),
          () => onOpenMilestone(step.milestone),
          { testId: `project-status-conflict-${step.milestone.id}`, secondary: true }
        ));
      }
      case 'sign':
        return line(t(isPerson ? 'projectView.overview.next.signPerson' : 'projectView.overview.next.signCompany'), action(
          t('projectView.overview.next.signButton'), () => onSign(step.contract),
          { testId: `project-status-sign-${step.contract.id}`, disabled: signingDisabled }
        ));
      case 'pay':
        return line(step.amount > 0
          ? t('projectView.overview.next.payAmount', { amount: formatCurrency(step.amount) })
          : t('projectView.overview.next.pay'), action(
          t('projectView.overview.next.payButton'), () => onPay(step.contract),
          { testId: `project-status-pay-${step.contract.id}`, secondary: true }
        ));
      case 'waitSignature':
        if (isViewer) return line(t('projectView.overview.next.waitSignatures'), undefined, true);
        return line(t(isPerson ? 'projectView.overview.next.waitCompanySignature' : 'projectView.overview.next.waitCustomerSignature'), undefined, true);
      case 'waitPayment':
        return line(t('projectView.overview.next.waitPayment'), undefined, true);
      default:
        return line(t('projectView.overview.next.nothing'), undefined, true);
    }
  };

  // Right side: what the viewer does. Waiting objections move to the left,
  // and a contract with other actions does not also say "nothing to do".
  const nextGroups = rows.map(row => {
    const extra = renderContractActions?.([row.contract.id, row.root.id]) ?? null;
    const steps = row.nextSteps.filter(step => !isWaitingObjection(step));
    const shown = extra ? steps.filter(step => step.kind !== 'nothing') : steps;
    return { row, extra, steps: shown.length || extra ? shown : row.nextSteps.length ? [{ kind: 'nothing' } as OverviewNextStep] : [] };
  }).filter(group => group.steps.length > 0 || group.extra);
  const waitingObjections = rows.flatMap(row => row.nextSteps.filter(isWaitingObjection)
    .map(step => ({ row, milestone: (step as Extract<OverviewNextStep, { kind: 'conflict' }>).milestone })));
  const payableDetail = totals.payable > 0 && totals.payable < totals.amount
    ? t('combinedTimeline.totalsAfterRot', { amount: formatCurrency(totals.payable) })
    : t('projectView.overview.kpi.contractCount', { count: totals.count });
  const outstandingContracts = rows.filter(row => row.counted && row.outstanding > 0)
    .map(row => t('projectView.overview.contractNumber', { number: row.root.contractNumber })).join(', ');
  type Kpi = { key: string; label: string; value: number; detail?: string; tone?: 'warning' | 'success' };
  const contractValue: Kpi = { key: 'amount', label: t('projectView.overview.kpi.total'), value: totals.amount, detail: payableDetail };
  // A lawyer or inspector sees what the contracts are worth, not the payments.
  const kpis: Kpi[] = isViewer ? [contractValue] : [
    contractValue,
    {
      key: 'paidIn', label: t(isPerson ? 'projectView.overview.kpi.paidInPerson' : 'projectView.overview.kpi.paidInCompany'),
      value: totals.paidIn,
      detail: [
        t('projectView.overview.kpi.of', { amount: formatCurrency(totals.payable) }),
        pendingPayment > 0 ? t('projectView.overview.kpi.pendingPayment', { amount: formatCurrency(pendingPayment) }) : null
      ].filter(Boolean).join(' · ')
    },
    {
      key: 'paidOut', label: t(isPerson ? 'combinedTimeline.totalsPaidOutPerson' : 'combinedTimeline.totalsPaidOutCompany'),
      value: totals.paidOut,
      detail: totals.reserved > 0 ? t('projectView.overview.kpi.reserved', { amount: formatCurrency(totals.reserved) }) : undefined,
      tone: totals.payable > 0 && totals.paidOut >= totals.payable ? 'success' : undefined
    },
    isPerson ? {
      key: 'outstanding', label: t('projectView.overview.kpi.outstandingPerson'), value: totals.outstanding,
      detail: outstandingContracts || undefined, tone: totals.outstanding > 0 ? 'warning' : undefined
    } : {
      key: 'remaining', label: t('projectView.overview.kpi.remainingCompany'), value: totals.remainingPayout,
      detail: totals.outstanding > 0 ? t('projectView.overview.kpi.notPaidIn', { amount: formatCurrency(totals.outstanding) }) : undefined
    }
  ];
  const columns = isViewer ? [t('projectView.overview.table.amount')] : [
    t('projectView.overview.table.amount'),
    t(isPerson ? 'projectView.overview.table.paidInPerson' : 'projectView.overview.table.paidInCompany'),
    t(isPerson ? 'projectView.overview.table.paidOutPerson' : 'projectView.overview.table.paidOutCompany')
  ];
  const figures = (row: Pick<OverviewContractRow, 'amount' | 'paidIn' | 'paidOut'>) =>
    isViewer ? [row.amount] : [row.amount, row.paidIn, row.paidOut];
  // An unsigned contract shows its sum, but adds nothing to the totals.
  // The narrow cards leave out the brackets; the muted colour and the note say it.
  const cell = (row: OverviewContractRow, value: number, index: number, compact = false) => row.counted || (compact && index === 0)
    ? formatCurrency(value)
    : index === 0 ? t('projectView.overview.table.uncountedAmount', { amount: formatCurrency(value) }) : '–';
  const hasUncounted = rows.some(row => !row.counted);

  return (
    <section className="card project-status mb-4" aria-labelledby="project-status-title" data-testid="project-status-overview">
      <header className="project-status-header">
        <h2 id="project-status-title" className="project-status-title">
          <i className="feather-folder" aria-hidden="true"></i>
          {t('projectView.overview.title')}
        </h2>
        {statusBadge}
        <span className="project-status-scope">{t('projectView.overview.scope')}</span>
        {menuItems.length > 0 && (
          <div className="project-status-menu" ref={menuRef}>
            <button type="button" className="project-status-menu-toggle" aria-expanded={menuOpen} aria-haspopup="true"
              aria-controls="project-status-menu-list" data-testid="project-status-menu-toggle" onClick={() => setMenuOpen(!menuOpen)}>
              {t('projectView.overview.moreActions')}
              <i className={`bi bi-chevron-${menuOpen ? 'up' : 'down'} ms-2`} aria-hidden="true" />
            </button>
            <ul id="project-status-menu-list" className="project-status-menu-list list-unstyled" hidden={!menuOpen}>
              {menuItems.map(item => (
                <li key={item.key}>
                  <button type="button" data-testid={`project-status-menu-${item.key}`}
                    onClick={() => { setMenuOpen(false); item.onClick(); }}>{item.label}</button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </header>

      <div className="project-status-body">
        <div className="project-status-lead">
          <div className="project-status-summary">
            <p className="project-status-sentence" data-testid="project-status-sentence">{sentence}</p>
            {notices}
            {waitingObjections.length > 0 && (
              <div className="project-status-waiting" data-testid="project-status-waiting">
                {waitingObjections.map(({ row, milestone }) => (
                  <React.Fragment key={milestone.id}>
                    {objectionNotice(milestone, t('projectView.overview.contractNumber', { number: row.root.contractNumber }))}
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
          <div className="project-status-next" data-testid="project-status-next">
            <h3 className="project-status-label">{t(isViewer ? 'projectView.overview.nextViewer' : isPerson ? 'projectView.overview.nextPerson' : 'projectView.overview.nextCompany')}</h3>
            {nextGroups.length === 0 ? (
              <p className="project-status-next-text is-quiet mb-0">{t('projectView.overview.next.nothing')}</p>
            ) : <div className="project-status-next-groups">
              {nextGroups.map(({ row, steps, extra }) => (
                <div key={row.contract.id} className="project-status-next-group" data-testid={`project-status-next-${row.contract.id}`}>
                  <div className="project-status-next-contract">{contractName(row)}</div>
                  {steps.length > 0 && <ul className="list-unstyled mb-0">{steps.map((step, index) => renderStep(row, step, index))}</ul>}
                  {extra}
                </div>
              ))}
            </div>}
          </div>
        </div>

        <div className="project-status-section">
          <h3 className="project-status-label">{t('projectView.overview.totalsTitle')}</h3>
          <dl className="project-status-kpis" data-testid="project-status-kpis">
            {kpis.map(kpi => (
              <div key={kpi.key} className={`project-status-kpi${kpi.tone ? ` is-${kpi.tone}` : ''}`} data-project-kpi={kpi.key}>
                <dt>{kpi.label}</dt>
                <dd className="project-status-kpi-value">{formatCurrency(kpi.value)}</dd>
                {kpi.detail && <dd className="project-status-kpi-detail">{kpi.detail}</dd>}
              </div>
            ))}
          </dl>
        </div>

        {rows.length > 0 && (
          <div className="project-status-section">
            <h3 className="project-status-label">{t('projectView.overview.perContract')}</h3>
            <div className="project-status-table-wrap">
              <table className="table project-status-table mb-0" data-testid="project-status-table">
                <thead>
                  <tr>
                    <th scope="col">{t('projectView.overview.table.contract')}</th>
                    <th scope="col">{t('projectView.overview.table.status')}</th>
                    {columns.map(column => <th key={column} scope="col" className="text-end">{column}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {rows.map(row => (
                    <tr key={row.contract.id} className={row.counted ? undefined : 'is-uncounted'} data-project-contract-row={row.contract.id}>
                      <td>
                        <button type="button" className="btn btn-link project-status-contract-link" onClick={() => onShowContract(row.root)}>
                          {contractName(row)}
                        </button>
                        {amendments(row) && <span className="project-status-amendments">{amendments(row)}</span>}
                      </td>
                      <td><span className={`contract-group-status-pill ${STATUS_TONE[row.status]}`}>{statusLabel(row)}</span></td>
                      {figures(row).map((value, index) => <td key={index} className="text-end">{cell(row, value, index)}</td>)}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={2}>{t('projectView.overview.table.total')}</td>
                    {figures(totals).map((value, index) => <td key={index} className="text-end">{formatCurrency(value)}</td>)}
                  </tr>
                </tfoot>
              </table>
            </div>
            <ul className="project-status-cards list-unstyled mb-0">
              {rows.map(row => (
                <li key={row.contract.id} className={`project-status-card${row.counted ? '' : ' is-uncounted'}`}>
                  <div className="project-status-card-top">
                    <button type="button" className="btn btn-link project-status-contract-link" onClick={() => onShowContract(row.root)}>
                      {contractName(row)}
                    </button>
                    <span className={`contract-group-status-pill ${STATUS_TONE[row.status]}`}>{statusLabel(row)}</span>
                  </div>
                  {amendments(row) && <span className="project-status-amendments">{amendments(row)}</span>}
                  <dl className="project-status-card-figures">
                    {figures(row).map((value, index) => <div key={index}><dt>{columns[index]}</dt><dd>{cell(row, value, index, true)}</dd></div>)}
                  </dl>
                </li>
              ))}
              <li className="project-status-card is-total">
                <div className="project-status-card-top"><strong>{t('projectView.overview.table.total')}</strong></div>
                <dl className="project-status-card-figures">
                  {figures(totals).map((value, index) => <div key={index}><dt>{columns[index]}</dt><dd>{formatCurrency(value)}</dd></div>)}
                </dl>
              </li>
            </ul>
            {hasUncounted && <p className="project-status-note">{t('projectView.overview.table.uncountedNote')}</p>}
          </div>
        )}
      </div>
    </section>
  );
}
